import cx from 'classnames';
import { getFlowSinkIds } from 'js/actions/rag';
import styles from 'js/components/pipelines/cards/PipelineOriginCard.module.scss';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import { ReactComponent as ArrowIcon } from 'new/components/icons/arrow.svg';
import { ReactComponent as ArrowRightIcon } from 'new/components/icons/longArrowRight.svg';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import useEntities from 'new/state/entities/repository/useEntities';
import React, { useMemo } from 'react';

interface ResourceCountersProps {
  isClickablePartHovered?: boolean;
  data: any;
  flowType: string;
}

const resourceLabels = {
  api_server: {
    api_request: 'Request',
    api_success_modifier: 'Transform',
    api_target: 'Connector',
    api_result: 'Result',
  },
  rag: {
    query_rewriter: 'Query Rewriter',
    data_retriever_holder: 'Data Retriever',
    context_enricher: 'Context Enricher',
    llm_processor: 'LLM Processor',
  },
};

function buildPipelineComponents(data, entities) {
  const components = [];

  if (!data?.children?.length) {
    return components;
  }

  let currentData = data;
  const labels = resourceLabels.rag;

  while (currentData && currentData.children?.length > 0) {
    const nextData = getResourceEntity(entities.flowNodes[currentData.children[0]], entities, true);

    if (!nextData) break;

    const isRetriever = nextData?.node_type === 'data_retriever_holder';
    const isLLM = nextData?.node_type === 'llm_processor';
    const flow = entities.flowNodes[nextData?.origin_node_id];
    const retrieversCount = isRetriever ? flow?.linked_flows?.length || 0 : undefined;
    const llmSinkIds = getFlowSinkIds(flow, entities.flowNodes);
    const llmCount = isLLM ? llmSinkIds.length : undefined;

    const count = retrieversCount ?? llmCount ?? 1;

    components.push({
      nodeType: nextData.node_type,
      count,
      name: labels?.[nextData.node_type],
    });

    currentData = nextData;
  }

  const hasLLMProcessor = components.some((comp) => comp.nodeType === 'llm_processor');
  if (!hasLLMProcessor) {
    components.push({
      nodeType: 'llm_processor',
      count: 0,
      name: labels?.llm_processor,
    });
  }

  return components;
}

export const ResourceCounters = (props: ResourceCountersProps) => {
  const { isClickablePartHovered, data, flowType } = props;
  const { data: entities } = useEntities();
  const counters = useMemo(() => {
    const components = buildPipelineComponents(data, entities);

    return components.map((component, index) => (
      <React.Fragment key={component.nodeType}>
        <div>
          <div className={styles.circle}>{component.count}</div>
          <Typography variant="caption" color="primary.dark">
            {component.name}
            {component.count === 1 ? '' : 's'}
          </Typography>
        </div>
        {index < components.length - 1 && <ArrowRightIcon />}
      </React.Fragment>
    ));
  }, [data, entities]);

  // function for api server flows (total aggregation)
  function getApiServerTotals(rootData) {
    const totals: Record<string, number> = {};
    const labels = resourceLabels.api_server;

    Object.keys(labels).forEach((nodeType) => {
      totals[nodeType] = 0;
    });

    function traverseNodes(nodeData) {
      if (!nodeData) return;

      const nodeEntity = getResourceEntity(
        entities.flowNodes[nodeData.id] || nodeData,
        entities,
        true,
      );

      // Splitter outputs behave like transform-stage Nexsets in the summary.
      // Keep their distinct node type on the canvas, but fold their count into
      // the existing Transform bucket instead of adding another summary row.
      const summaryNodeType =
        nodeEntity?.node_type === 'splitter_output'
          ? 'api_success_modifier'
          : nodeEntity?.node_type;

      if (summaryNodeType && labels[summaryNodeType]) {
        totals[summaryNodeType] = (totals[summaryNodeType] || 0) + 1;
      }

      if (nodeEntity?.children?.length) {
        nodeEntity.children.forEach((childId) => {
          const childNode = entities.flowNodes[childId];
          if (childNode) {
            traverseNodes(childNode);
          }
        });
      }
    }

    traverseNodes(rootData);
    const totalEntries = Object.entries(totals).map(([nodeType, count]) => ({
      nodeType,
      label: labels[nodeType],
      count: count as number,
    }));

    return totalEntries.map((entry, index) => (
      <React.Fragment key={entry.nodeType}>
        <div>
          <div className={styles.circle}>{entry.count}</div>
          <Typography variant="caption" color="primary.dark">
            {entry.label}
            {entry.count === 1 ? '' : 's'}
          </Typography>
        </div>
        {index < totalEntries.length - 1 && <ArrowRightIcon />}
      </React.Fragment>
    ));
  }

  return (
    <div className={cx(styles.counters, isClickablePartHovered && styles.countersHovered)}>
      <ArrowIcon className={styles.arrowOpen} />
      {flowType === 'api_server' ? getApiServerTotals(data) : counters}
    </div>
  );
};
